import { FC, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';

import { AppRoutes, PageSpinner, ProviderRoot } from '@app/components';
import { PatronSession, useAppUsers, useFeatureFlag } from '@app/hooks';
import { parseJSON } from '@app/utils';
import { FEATURE, IntlMessages, SetState, USER_SESSION } from '@app/constants';

import { ThemingConfig } from './ThemingConfig';

interface AppReadyProps {
  locale: string;
  translationMessages: IntlMessages;
  bannerMessage: string;
  setSelectedLocale: SetState<string>;
  setIsOkapiSessionDown: SetState<boolean>;
}

export type PatronSessionState = PatronSession | null;

export const AppReady: FC<AppReadyProps> = ({
  locale,
  translationMessages,
  bannerMessage,
  setSelectedLocale,
  setIsOkapiSessionDown,
}) => {
  const [patronSession, setPatronSession] = useState<PatronSessionState>(null);
  const { isActive: isConsortiaLogin } = useFeatureFlag({
    name: FEATURE.consortiaLoginFlow,
  });
  const {
    isPatronUserLoggedIn,
    isTransferSavedActivityLoading,
    authenticateToken,
  } = useAppUsers({
    patronSession,
    isConsortiaLogin,
    setPatronSession,
    setIsOkapiSessionDown,
  });
  const patronSessionFromStorage = parseJSON(
    sessionStorage.getItem(USER_SESSION.patron),
    {}
  );

  const appContext = useMemo(
    () => ({
      locale,
      isLoggedIn: isPatronUserLoggedIn,
      patronSession,
      bannerMessage,
      setSelectedLocale,
      setPatronSession,
      authenticateToken,
    }),
    [
      locale,
      isPatronUserLoggedIn,
      patronSession,
      bannerMessage,
      setSelectedLocale,
      setPatronSession,
      authenticateToken,
    ]
  );

  if (
    (!isEmpty(patronSessionFromStorage) && !isPatronUserLoggedIn) ||
    isTransferSavedActivityLoading
  ) {
    return <PageSpinner />;
  }

  return (
    <ProviderRoot appContext={appContext} messages={translationMessages}>
      <ThemingConfig />
      <AppRoutes />
    </ProviderRoot>
  );
};
